"use client";

// Client-side telemetry for command invocations. We buffer events
// and flush in microbatches so a burst of hotkey presses doesn't
// turn into a per-invocation network round-trip. The server side
// fans the same payload into ClickHouse (`command_invocations`).
//
// Failures are best-effort: never block the UI on a stuck telemetry
// pipe.

type ClerkWindow = Window & {
  Clerk?: { session?: { getToken: () => Promise<string | null> } | null };
};

export interface CommandTelemetryEvent {
  command_id: string;
  source: string;
  context_scope: string;
  duration_ms: number;
  succeeded: boolean;
  error_code: string;
}

let buffer: CommandTelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 1500;

async function getToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const w = window as ClerkWindow;
  const s = w.Clerk?.session;
  if (!s) return null;
  try {
    return await s.getToken();
  } catch {
    return null;
  }
}

async function flush() {
  flushTimer = null;
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    const token = await getToken();
    await fetch("/api/v1/telemetry/commands", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // Discard on transport failure — telemetry is non-essential.
    // We don't re-queue: a backed-up tab shouldn't replay events
    // hours later with stale durations.
  }
}

export function emitTelemetry(event: CommandTelemetryEvent) {
  buffer.push(event);
  if (flushTimer == null) {
    flushTimer = setTimeout(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
  }
}

// flushOnUnload registers a beforeunload + pagehide handler that
// drains the buffer with sendBeacon. Without it, the last keystroke
// before the user navigates away gets lost.
export function installTelemetryFlush() {
  if (typeof window === "undefined") return;
  const handler = () => {
    if (buffer.length === 0) return;
    try {
      const blob = new Blob([JSON.stringify({ events: buffer })], {
        type: "application/json",
      });
      navigator.sendBeacon?.("/api/v1/telemetry/commands", blob);
    } catch {
      // ignore
    }
    buffer = [];
  };
  window.addEventListener("pagehide", handler);
  window.addEventListener("beforeunload", handler);
}
